import mongoose from "mongoose";
import * as uuid from "uuid";
import { match } from "@mssi/pssp-shared";

export interface ContractCatalogItemMainHardware {
  sku: string;
  localizationCodes: string[];
  name: string;
  category: string;
  operationSystem?: string; // sic - operatingSystem
  processor?: string;
  memory?: string;
  internalStorage?: string;
  display?: string;
  graphics?: string;
  dock?: string;
  imageUrls: string[];
  accessories?: string;
}

export interface ContractCatalogItemAccessory {
  _id: mongoose.Schema.Types.ObjectId;
  sku: string;
  name: string;
  category: string;
  description: string;
  imageUrl?: string;
  bigDealPrice?: number | string;
  bundleBigDealPriceOrdered?: number | string;
}

export interface ContractCatalogItemService {
  _id: mongoose.Schema.Types.ObjectId;
  parentId?: string | number;
  parentType: string;
  sku?: string;
  serviceOffer: string;
  description1?: string;
  description2?: string;
  description3?: string;
  description4?: string;
}

export interface ContractCatalogItemDisplay {
  _id: mongoose.Schema.Types.ObjectId;
  displayId: string | number;
  sku: string;
  name: string;
  category: string;
  nativeResolution: string;
  contractRatio: string; // sic - contrastRatio
  signalInputConnectors: string;
  imageUrl: string;
  bigDealPrice?: number | string;
  bigDealPriceOrdered?: number | string;
}

export interface ContractCatalogItem {
  _id: mongoose.Schema.Types.ObjectId;
  bundleId: string | number;
  bundleQuantity?: number;
  configId?: string;
  solutionId: string | number;
  bundleName: string;
  bundleDescription: string;
  bundleCurrency?: string;
  bundleCurrencyOrdered?: string;
  bundlePricePerMonth?: number;
  bundlePricePerMonthOrdered?: number;
  bundleContractLength: number;
  country: string;
  bundleBigDealPrice?: number;
  bundleBigDealPriceOrdered?: number;
  quantityFromContract?: string;
  quantityConsumed?: string;
  quantityAvailable?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  localizationCode?: any; // expect string
  mainHardware: ContractCatalogItemMainHardware;
  accessories: ContractCatalogItemAccessory[];
  services: ContractCatalogItemService[];
  displays: ContractCatalogItemDisplay[];
}

export interface ContractAdditionalInfo {
  _id: mongoose.Schema.Types.ObjectId;
  id: string;
  country?: string;
  bigDealNumber?: string;
  hpfsNumber?: string;
  endCustomerAccountNumber?: string;
}

export interface ContractDocument {
  _id: false;
  documentId: mongoose.Schema.Types.ObjectId;
  documentType: string;
  fileName: string;
  folder?: string;
}

export interface ContractSite {
  _id: mongoose.Schema.Types.ObjectId;
  name1: string;
  name2?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateOrProvince?: string;
  postalCode?: string;
  country: string;
  cbnCrs?: string;
  siteName?: string;
  billTo?: string;
}

export interface ContractDocuments {
  documentId: mongoose.Schema.Types.ObjectId;
  documentType: string;
  fileName: string;
  folder?: string;
  uploadDate?: string;
}

export interface Contract {
  _id: mongoose.Schema.Types.ObjectId;
  id: string;
  masterAgreementId?: string;
  _text: string;
  itsmCustomerId?: string;
  customerId?: string;
  name: string;
  contractId: string;
  bigDealNumber?: string;
  /** @deprecated */
  isCBA?: boolean;
  validFrom?: string;
  validTo?: string;
  salNumber?: string;
  country?: string;
  partnerId?: string;
  duration?: number;
  catalog: ContractCatalogItem[];
  sites: ContractSite[];
  additionalInfo: ContractAdditionalInfo[];
  documents?: ContractDocument[];
}

export const ContractCatalogItemSchema = new mongoose.Schema<ContractCatalogItem>({
  bundleId: mongoose.Schema.Types.Mixed,
  bundleQuantity: Number, // legacy only
  configId: String, // legacy only
  solutionId: mongoose.Schema.Types.Mixed,
  bundleName: String,
  bundleDescription: String,
  bundleCurrency: String,
  bundleCurrencyOrdered: String,
  bundlePricePerMonth: Number,
  bundlePricePerMonthOrdered: Number,
  bundleContractLength: Number,
  country: String,
  bundleBigDealPrice: Number,
  bundleBigDealPriceOrdered: Number,
  quantityFromContract: String,
  quantityConsumed: String,
  quantityAvailable: String,
  localizationCode: mongoose.Schema.Types.Mixed, // should be string[]
  mainHardware: {
    sku: String,
    localizationCodes: [String],
    name: String,
    category: String,
    operationSystem: String,
    processor: String,
    memory: String,
    internalStorage: String,
    display: String,
    graphics: String,
    dock: String,
    imageUrls: mongoose.Schema.Types.Array,
    accessories: String,
  },
  accessories: [
    {
      sku: String,
      name: String,
      category: String,
      description: String,
      imageUrl: String,
      bigDealPrice: Number,
      bigDealPriceOrdered: Number,
    },
  ],
  services: [
    {
      parentId: mongoose.Schema.Types.Mixed,
      parentType: String,
      sku: String,
      serviceOffer: String,
      description1: String,
      description2: String,
      description3: String,
      description4: String,
    },
  ],
  displays: [
    {
      displayId: Number,
      sku: String,
      name: String,
      category: String,
      nativeResolution: String,
      contractRatio: String,
      signalInputConnectors: String,
      imageUrl: String,
      bigDealPrice: Number,
      bigDealPriceOrdered: Number,
    },
  ],
});

export interface FilterCatalogItemMainHardware extends Pick<ContractCatalogItem, 'bundleContractLength' | 'country'> {
  category: ContractCatalogItemMainHardware['category'];
}
 
export interface SearchContractCatalogItem extends Pick<ContractCatalogItem, 'bundleName' | 'bundlePricePerMonth'>,
 Pick<ContractCatalogItemMainHardware, 'sku' | 'name'> {}

export const ContractAdditionalInfoSchema = new mongoose.Schema<ContractAdditionalInfo>({
  id: String,
  country: String,
  bigDealNumber: String,
  hpfsNumber: String,
  endCustomerAccountNumber: String,
});

export const ContractDocumentSchema = new mongoose.Schema<ContractDocument>({
  _id: false,
  documentId: mongoose.Schema.Types.ObjectId,
  documentType: String,
  fileName: String,
  folder: String,
});

export const ContractSiteSchema = new mongoose.Schema<ContractSite>({
  name1: String,
  name2: String,
  addressLine1: String,
  addressLine2: String,
  city: String,
  stateOrProvince: String,
  postalCode: String,
  country: String,
  cbnCrs: String, // ship to / scid
  siteName: String,
  billTo: String,
});

export const ContractSchema = new mongoose.Schema<Contract>({
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
  id: { type: String, index: true, default: () => uuid.v1() },
  masterAgreementId: String,
  _text: String,
  itsmCustomerId: { type: String, index: true }, // CBA only
  customerId: { type: String, index: true }, // legacy only
  name: String,
  contractId: String,
  bigDealNumber: String,
  /** @deprecated - infer from presence of masterAgreementId / itsmCustomerId */
  isCBA: Boolean,
  validFrom: String,
  validTo: String,
  salNumber: String,
  country: String,
  partnerId: String,
  duration: Number,
  catalog: [ContractCatalogItemSchema],
  sites: [ContractSiteSchema],
  additionalInfo: [ContractAdditionalInfoSchema],
  documents: [ContractDocumentSchema],
});

const props: (keyof Contract)[] = [
  "_text",
  "itsmCustomerId",
  "name",
  "contractId",
  "bigDealNumber",
  "isCBA",
  "validFrom",
  "validTo",
  "salNumber",
  "country",
  "partnerId",
  "duration",
];

export function matches(a: Contract, b: Contract) {
  return match(a, b, props);
}

export const searchProps: (keyof ContractSite)[] = [
  "siteName",
  "name1",
  "name2",
  "addressLine1",
  "addressLine2",
  "postalCode",
  "cbnCrs",
  "billTo"
];

export const filterProps: (keyof ContractSite)[] = [
  "city",
  "stateOrProvince",
  "country",
];

export const contractUtils = {
  props,
  matches,
};

export const contractSiteUtils ={
  searchProps,
  filterProps,
}

export const catalogSearchProps: (keyof SearchContractCatalogItem)[] = [
  "sku",
  "name",
  "bundleName",
  "bundlePricePerMonth",
];
 
export const catalogFilterProps: (keyof FilterCatalogItemMainHardware)[] = [
  "category",
  "bundleContractLength",
  "country",
];
 
export const contractCatalogUtils = {
  catalogSearchProps,
  catalogFilterProps,
};

export const documentsSearchProps: (keyof ContractDocuments)[] = [
  "fileName",
  "uploadDate",
];

export const documentsFilterProps: (keyof ContractDocuments)[] = [
  "documentType",
];

export const contractDocumentsUtils = {
  documentsSearchProps,
  documentsFilterProps,
};
